import { Component, type ReactNode } from "react";
import { type Locale, translate } from "../i18n/index";
import { reportError, shortId } from "../ops/report";

interface Props {
  locale: Locale;
  children: ReactNode;
}

interface State {
  failed: boolean;
  /** Reported to the server; the player can quote it (review 0019 S8). */
  eventId: string | null;
}

/** Last line of defence: a render error shows a message and a reload instead of a blank frame. */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false, eventId: null };

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    this.setState({ eventId: reportError("boundary", error) });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    const { locale } = this.props;
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-4 bg-night p-4 font-ui text-ink"
        role="alert"
      >
        <p>{translate(locale, "error.boundary")}</p>
        {this.state.eventId !== null && (
          <p className="text-[12px] text-dim">
            {translate(locale, "error.eventId", { id: shortId(this.state.eventId) })}
          </p>
        )}
        <button
          type="button"
          className="h-11 rounded-pill bg-mint px-6 text-[14px] font-bold text-mint-ink"
          onClick={() => window.location.reload()}
        >
          {translate(locale, "error.reload")}
        </button>
      </div>
    );
  }
}
